import { YAxisOptions } from "highcharts";
import { MeasurementType } from "../../shared/enums/Result";
import { UTP2EventHeadings } from "../../shared/enums/UTP2";
import { UTP2Data, UTP2Events } from "../../shared/interfaces/UTP2";

export interface PlotValue {
  x: number;
  y: number;
  z: number;
}

/**
 * Calculates the Ultrasonic phase plots for UTP2 versions below v4.0.
 **/
export function buildUltrasonicPhasePlots(utp2Data: UTP2Data) {
  const minamp = -10;
  const plotValues: PlotValue[] = [];

  utp2Data.data.forEach((valueArray, dataIndex) => {
    valueArray.forEach((value, valueIndex) => {
      if (value > 0) {
        plotValues.push({
          x: valueIndex,
          y: minamp + (dataIndex * 1) / 3,
          z: value,
        });
      }
    });
  });

  return plotValues;
}

/**
 * Shifts the amplitude value to reduce resolution for Ultrasonic measurements.
 * This amplitude shift is also being done in the UTP2 result javascript for Ultrasonic measurements - see below comment from there for explanation:
 * "Amplitude is floating point and has too much resolution, so round amplitude to 1/3 dB so that there is some grouping to show as hotspots on the colour map."
 **/
function shiftUltrasonicAmplitude(value: number) {
  return Math.round(value * 3) / 3;
}

export function buildPhasePlots(
  events: UTP2Events,
  measurementType: MeasurementType,
  riseTime?: [number, number],
  pulseWidth?: [number, number],
) {
  const headingIndices = getHeadingIndices(events.headings, measurementType);
  const riseTimeHeader = events.headings.findIndex((heading) => heading === UTP2EventHeadings.RiseTime);
  const pulseWidthHeader = events.headings.findIndex((heading) => heading === UTP2EventHeadings.PulseWidth);
  const plotValues: PlotValue[] = events?.data.reduce((accumulator: PlotValue[], current) => {
    const xValue = current[headingIndices.x];
    let yValue = current[headingIndices.y];
    const risetime = current[riseTimeHeader];
    const width = current[pulseWidthHeader];

    if (
      (riseTime !== undefined && (risetime < riseTime[0] || risetime > riseTime[1])) ||
      (pulseWidth !== undefined && (width < pulseWidth[0] || width > pulseWidth[1]))
    ) {
      return accumulator;
    }

    if (measurementType === MeasurementType.Ultra) {
      yValue = shiftUltrasonicAmplitude(yValue);
    } else if (measurementType === MeasurementType.VPIS) {
      yValue = Math.round(yValue);
    }

    const foundValue = accumulator.find((record) => record.x === xValue && record.y === yValue);

    if (!foundValue) {
      return [...accumulator, { x: xValue, y: yValue, z: 1 }];
    } else {
      foundValue.z += 1;

      return accumulator;
    }
  }, []);

  return plotValues;
}

export function getSineWavePlots(ymax: number, ymin: number, sineWaves: 0 | 1 | 3, measurementType: MeasurementType) {
  let offset = (ymin + ymax) / 2;
  let amp = (ymax - ymin) / 2;
  let sinedata: number[][][] = [[], [], []];

  for (let i = 0; i < 360; i++) {
    sinedata[0].push([i, Math.sin(Math.PI * (i / 180))]);
    sinedata[1].push([i, Math.sin(Math.PI * ((i - 120) / 180))]);
    sinedata[2].push([i, Math.sin(Math.PI * ((i - 240) / 180))]);
  }

  // TEV, Ultra and UHF should have unipolar sinewaves.
  if (
    measurementType === MeasurementType.TEV ||
    measurementType === MeasurementType.Ultra ||
    measurementType === MeasurementType.UHF
  ) {
    offset = ymin;
    amp = ymax - ymin;
    sinedata[0] = sinedata[0].map((el) => [el[0], Math.abs(el[1])]);
    sinedata[1] = sinedata[1].map((el) => [el[0], Math.abs(el[1])]);
    sinedata[2] = sinedata[2].map((el) => [el[0], Math.abs(el[1])]);
  }

  sinedata[0] = sinedata[0].map((el) => [el[0], offset + amp * el[1]]);
  sinedata[1] = sinedata[1].map((el) => [el[0], offset + amp * el[1]]);
  sinedata[2] = sinedata[2].map((el) => [el[0], offset + amp * el[1]]);

  let sineWavePlots = [] as Partial<Highcharts.SeriesOptionsType>[];

  sineWavePlots.push(makeSineplot(sineWaves === 1, "Single phase sine", sinedata[0], "#000000"));

  sineWavePlots.push(makeSineplot(sineWaves === 3, "Three phase sine 1", sinedata[0], "#FF0000"));
  sineWavePlots.push(makeSineplot(sineWaves === 3, "Three phase sine 2", sinedata[1], "#FFFF00"));
  sineWavePlots.push(makeSineplot(sineWaves === 3, "Three phase sine 3", sinedata[2], "#0000FF"));

  return sineWavePlots;
}

function makeSineplot(visible: boolean, name: string, sinedata: number[][], colour: string) {
  return {
    visible: visible,
    name: name,
    data: sinedata,
    color: colour,
    type: "line",
    marker: {
      enabled: false,
    },
    states: {
      hover: {
        lineWidth: 0,
      },
    },
    enableMouseTracking: false,
    animation: false,
    showInLegend: false,
    includeInDataExport: false,
  } as Partial<Highcharts.SeriesLineOptions>;
}

export function buildPhaseRangeSeries(
  plotValues: PlotValue[],
  yAxisOptions: YAxisOptions | undefined,
  phaseShift: number = 0,
  sineWaves: 0 | 1 | 3 = 0,
  measurementType: MeasurementType,
) {
  const maxZ = Math.max(...plotValues.map((o) => o.z), 0);

  const ranges = [
    {
      min: 0,
      max: maxZ * 0.1,
      colour: "#6AC04B", //green
    },
    {
      min: maxZ * 0.1,
      max: maxZ * 0.45,
      colour: "#074F84", //blue
    },
    {
      min: maxZ * 0.45,
      max: maxZ * 0.8,
      colour: "#FB4E34", //red
    },
    {
      min: maxZ * 0.8,
      max: maxZ,
      colour: "#9D0006", //dark red
    },
  ];

  let graphData = ranges.map((range) => {
    const seriesData = plotValues
      .filter((value) => value.z > range.min && value.z <= range.max)
      .map((value) => [(value.x + 360 + phaseShift) % 360, value.y]);

    return {
      data: seriesData,
      name: buildSeriesName(range.min, range.max),
      color: range.colour,
    } as Partial<Highcharts.SeriesOptionsType>;
  });

  const absMax = Math.max(...plotValues.map((o) => Math.abs(o.y)), 0);
  // ymax and ymin should be set in the chart itself now, this is just here to make sure.
  const ymax = yAxisOptions?.max ?? absMax * 1.1;
  const ymin = yAxisOptions?.min ?? absMax * -1.1;
  return getSineWavePlots(ymax, ymin, sineWaves, measurementType).concat(graphData);
}

function buildSeriesName(min: number, max: number) {
  if (min === 0) return "< " + Math.round(max);
  else return Math.round(min) + " < " + Math.round(max);
}

export function getHeadingIndices(headings: string[], measurementType: MeasurementType) {
  if (measurementType === MeasurementType.CablePD)
    return {
      x: headings.findIndex((heading) => heading === UTP2EventHeadings.PhaseAngle),
      y: headings.findIndex((heading) => heading === UTP2EventHeadings.Integral),
    };
  else if (measurementType === MeasurementType.VPIS)
    return {
      x: headings.findIndex((heading) => heading === UTP2EventHeadings.PhaseAngle),
      y: headings.findIndex((heading) => heading === UTP2EventHeadings.PeakAmplitude),
    };
  else if (measurementType === MeasurementType.TEV || measurementType === MeasurementType.UHF)
    return {
      x: headings.findIndex((heading) => heading === UTP2EventHeadings.PhaseAngle),
      y: headings.findIndex((heading) => heading === UTP2EventHeadings.PeakAmplitude),
    };
  else if (measurementType === MeasurementType.Ultra)
    return {
      x: headings.findIndex((heading) => heading === UTP2EventHeadings.PhaseAngle),
      y: headings.findIndex((heading) => heading === UTP2EventHeadings.Amplitude),
    };
  else
    return {
      x: headings.findIndex((heading) => heading === UTP2EventHeadings.PhaseAngle),
      y: headings.findIndex((heading) => heading === UTP2EventHeadings.PeakAmplitude),
    };
}

export function getHeadingLimits(headingType: UTP2EventHeadings, events?: UTP2Events) {
  if (events == null || events.headings == null) return [0, 0];
  const headingIndex = events.headings.findIndex((heading) => heading === headingType);
  const headingValues = events?.data.map((test) => test[headingIndex]);

  return [Math.min(...headingValues), Math.max(...headingValues)];
}
